import { useCallback, useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableWithin(container: HTMLElement): HTMLElement[] {
  // Deliberately not an offsetParent or getBoundingClientRect check: jsdom performs no layout, so
  // those report every element as hidden and the trap would silently do nothing under test while
  // working in a browser. Attribute-based hiding is the part that actually matters here.
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    el => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true'
  );
}

/**
 * Gives a dialog the behaviour its `aria-modal="true"` already promises: focus moves inside on
 * open, Tab cycles within rather than escaping to the page behind, Escape dismisses, and focus
 * returns to whatever opened it.
 *
 * Deliberately a hook rather than a wrapper component. The modals that use it do not share
 * markup or class names, so a shared shell would have changed how the existing ones look.
 * This adds behaviour and touches nothing visual.
 *
 * @param isOpen whether the dialog is currently rendered.
 * @param onClose called on Escape. Omit to opt out of Escape dismissal.
 * @returns a ref to attach to the element carrying role="dialog".
 */
export default function useModalDialog<T extends HTMLElement = HTMLDivElement>(isOpen: boolean, onClose?: () => void) {
  const dialogRef = useRef<T>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);
  // Held in a ref so a caller passing an inline arrow does not re-run the effect on every render,
  // which would re-steal focus mid-interaction.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (event.key === 'Escape') {
      event.stopPropagation();
      onCloseRef.current?.();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = focusableWithin(dialog);
    if (focusable.length === 0) {
      // Nothing to move to, so keep focus on the dialog rather than letting it leave.
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (!active || !dialog.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const dialog = dialogRef.current;
    returnFocusTo.current = document.activeElement as HTMLElement | null;

    if (dialog) {
      const focusable = focusableWithin(dialog);
      // preventScroll so opening a dialog never yanks the page behind it.
      (focusable[0] ?? dialog).focus({ preventScroll: true });
    }

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);

      // Only restore if the opener is still on the page. After a reload-and-refresh the row that
      // opened the dialog may be gone, and focusing a detached node silently drops focus to body.
      const opener = returnFocusTo.current;
      returnFocusTo.current = null;
      if (opener && document.contains(opener)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [isOpen, handleKeyDown]);

  return dialogRef;
}
